import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, Check, FileCheck2, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TemperatureChart } from '@/components/landing/temperature-chart'
import { company } from '@/constants/company'
import { HERO_POINTS } from '@/constants/content'

type EventTone = 'signal' | 'neutral' | 'ok'

interface LogEvent {
    time: string
    text: string
    status: string
    tone: EventTone
}

const EVENTS: Array<LogEvent> = [
    { time: 't + 19 h', text: "Kern-Rand-Differenz erreicht 15 K", status: "Gemeldet", tone: 'signal' },
    { time: 't + 20 h', text: "Maßnahme: Abdeckung mit Dämmmatten", status: "Erfasst", tone: 'neutral' },
    { time: 't + 28 h', text: "Differenz wieder unter Grenzwert", status: "Entwarnung", tone: 'ok' },
    { time: 't + 48 h', text: "QS-Export Abschnitt B3", status: "Bereit", tone: 'ok' }
]

const TONES: Record<EventTone, string> = {
    signal: 'text-signal',
    neutral: 'text-muted-foreground',
    ok: 'text-primary'
}

const cardShadow = 'shadow-[0_1px_2px_rgb(18_28_26/0.04),0_32px_64px_-32px_rgb(18_28_26/0.32)]'

export const HeroSection: FC = (): ReactNode => {
    return (
        <section
            id='hero'
            aria-labelledby='hero-title'
            className='relative -mt-[4.75rem] overflow-hidden pt-[4.75rem]'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_90%_75%_at_50%_0%,black_30%,transparent_100%)]'
            />

            <div
                aria-hidden='true'
                className='pointer-events-none absolute -right-48 top-24 size-[38rem] rounded-full bg-primary/10 blur-3xl'
            />

            <div className='relative mx-auto grid max-w-6xl items-center gap-14 px-6 pb-20 pt-12 sm:pt-16 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-20'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out motion-reduce:animate-none lg:col-span-7'>
                    <p className='inline-flex rounded-full border border-border bg-card/70 px-3.5 py-1.5 font-mono text-[0.66rem] font-medium uppercase tracking-[0.16em] text-foreground/75 sm:text-[0.7rem]'>
                        Betontemperatur-Monitoring
                        <span className='hidden sm:inline'>&nbsp;· WU-Beton · Massenbauteile</span>
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-6 text-balance text-[clamp(2.15rem,9.4vw,3.4rem)] font-light leading-[1.03] tracking-[-0.03em] text-foreground lg:text-[3.7rem] xl:text-[4.1rem]'
                    >
                        Temperaturrisse vermeiden. Qualität lückenlos belegen.
                    </h1>

                    <p className='mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground'>
                        ConcreTrack misst die Temperatur direkt im frisch eingebauten Beton, über die kritischen ersten 24 bis 72 Stunden der Hydratation. Steigt die Kern-Rand-Differenz über den vereinbarten Grenzwert, je nach Vorgabe 15 bis 20&nbsp;K, werden Sie sofort gewarnt und können rechtzeitig nachbehandeln.
                    </p>

                    <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                        <Link
                            href='/#kontakt'
                            className='group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 font-medium text-primary-foreground shadow-[0_10px_24px_-12px] shadow-primary/70 transition-colors hover:bg-primary/90'
                        >
                            Projekt unverbindlich besprechen
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>

                        <a
                            href={company.phoneHref}
                            aria-label={`Direkt anrufen: ${company.phone}`}
                            className='inline-flex h-12 items-center justify-center gap-2 rounded-full border border-border bg-card px-6 font-medium text-foreground transition-colors hover:bg-muted'
                        >
                            <Phone className='size-4 text-primary' aria-hidden='true' />
                            {company.phone}
                        </a>
                    </div>

                    <ul className='mt-8 flex flex-wrap gap-2'>
                        {HERO_POINTS.map((point) => (
                            <li
                                key={point}
                                className='inline-flex items-center gap-1.5 rounded-full border border-border bg-card/70 px-3 py-1.5 text-sm text-foreground/80'
                            >
                                <Check className='size-4 text-primary' aria-hidden='true' />
                                {point}
                            </li>
                        ))}
                    </ul>

                    <p className='mt-6 flex items-center gap-2.5 text-sm text-muted-foreground'>
                        <span className='relative flex size-2 shrink-0' aria-hidden='true'>
                            <span className='absolute inline-flex size-full animate-ping rounded-full bg-primary/60 motion-reduce:animate-none' />
                            <span className='relative inline-flex size-2 rounded-full bg-primary' />
                        </span>
                        Im Einsatz: 7 überwachte Bodenplatten für zwei Bauunternehmen, 2026.
                    </p>
                </div>

                <div className='animate-in fade-in slide-in-from-bottom-8 duration-1000 ease-out motion-reduce:animate-none lg:col-span-5'>
                    <div className={cn('rounded-[1.75rem] border border-border bg-card p-5 sm:p-6', cardShadow)}>
                        <div className='flex items-start justify-between gap-4'>
                            <div>
                                <p className='font-mono text-[0.64rem] font-medium uppercase tracking-[0.18em] text-muted-foreground'>
                                    Projekt A14 · Abschnitt B3
                                </p>

                                <p className='mt-1.5 font-medium text-foreground'>
                                    Bauteiltemperatur · letzte 48 h
                                </p>

                                <p className='mt-0.5 text-[0.8rem] text-muted-foreground'>
                                    Bodenplatte, d = 1,20 m · 4 Sonden · Grenzwert 15 K
                                </p>
                            </div>

                            <span className='inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary/15 bg-accent px-2.5 py-1 font-mono text-[0.6rem] font-medium uppercase tracking-[0.14em] text-accent-foreground'>
                                <span className='size-1.5 rounded-full bg-primary' aria-hidden='true' />
                                Beispiel
                            </span>
                        </div>

                        <TemperatureChart />
                    </div>

                    <div className={cn('relative mt-4 rounded-[1.75rem] border border-border bg-card p-5 sm:p-6 lg:ml-10', cardShadow)}>
                        <div className='flex items-start justify-between gap-3'>
                            <div>
                                <p className='font-mono text-[0.64rem] font-medium uppercase tracking-[0.18em] text-muted-foreground'>
                                    Ereignisprotokoll
                                </p>

                                <p className='mt-1.5 font-medium text-foreground'>
                                    Übergabe an die Qualitätssicherung
                                </p>
                            </div>

                            <FileCheck2 className='size-5 shrink-0 text-primary' aria-hidden='true' />
                        </div>

                        <ol className='mt-4 grid gap-2'>
                            {EVENTS.map((event) => (
                                <li
                                    key={event.text}
                                    className='flex items-center gap-3 rounded-xl border border-border bg-background/60 px-3.5 py-2.5'
                                >
                                    <span className='w-14 shrink-0 font-mono text-[0.66rem] tabular-nums text-muted-foreground'>
                                        {event.time}
                                    </span>

                                    <span className='min-w-0 flex-1 text-[0.84rem] leading-snug text-foreground'>
                                        {event.text}
                                    </span>

                                    <span className={cn('shrink-0 font-mono text-[0.6rem] font-semibold uppercase tracking-[0.12em]', TONES[event.tone])}>
                                        {event.status}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    )
}
